'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState, useEffect } from 'react'
import {
  ChatCircle,
  GitBranch,
  Calendar,
  Books,
  Video,
  Microphone,
  Waveform,
  Brain,
  ChartBar,
  GearSix,
  FlowArrow,
  Pulse as PulseIcon,
} from '@phosphor-icons/react'
import { health } from '@/lib/api'
import { Spinner as CliSpinner } from '@/components/ui/cli-spinner'
import { useChatStore } from '@/store/chat-store'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const NAV_LINKS = [
  { href: '/chat', label: 'Chat', icon: ChatCircle },
  { href: '/workflows', label: 'Workflows', icon: FlowArrow },
  { href: '/genesis', label: 'Genesis', icon: GitBranch },
  { href: '/schedule', label: 'Schedule', icon: Calendar },
  { href: '/knowledge', label: 'Knowledge', icon: Books },
  { href: '/meetings', label: 'Meetings', icon: Video },
  { href: '/recordings', label: 'Recordings', icon: Microphone },
  { href: '/voice', label: 'Voice', icon: Waveform },
  { href: '/memory', label: 'Memory', icon: Brain },
  { href: '/traces', label: 'Traces', icon: PulseIcon },
  { href: '/usage', label: 'Usage', icon: ChartBar },
  { href: '/settings', label: 'Settings', icon: GearSix },
]

function SystemStatus() {
  const { isHealthy, setIsHealthy } = useChatStore()
  const [checking, setChecking] = useState(false)

  const check = async () => {
    setChecking(true)
    try {
      const result = await health.check()
      setIsHealthy(result.status === 'ok' || result.status === 'healthy')
    } catch {
      setIsHealthy(false)
    } finally {
      setChecking(false)
    }
  }

  useEffect(() => { check() }, [])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={check}
          disabled={checking}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors disabled:opacity-50"
        >
          {checking ? (
            <CliSpinner name="orbit" size="0.7rem" />
          ) : (
            <div className={`h-1.5 w-1.5 rounded-full ${isHealthy ? 'bg-green-500' : 'bg-red-500'}`} />
          )}
          <span className="text-xs">{isHealthy ? 'Connected' : 'Disconnected'}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {isHealthy ? 'Backend is reachable' : 'Backend unreachable — click to retry'}
      </TooltipContent>
    </Tooltip>
  )
}

export function TopBar() {
  const pathname = usePathname()

  return (
    <header className="flex h-11 items-center border-b border-border bg-background px-4 gap-6 shrink-0">
      {/* Logo → home */}
      <Link
        href="/"
        className="text-sm font-semibold text-foreground hover:text-muted-foreground transition-colors mr-2 shrink-0"
      >
        <img src="/kael-logo-with-text.svg" alt="Kael" className="h-6" />
      </Link>

      <nav className="flex items-center gap-0.5 flex-1">
        {NAV_LINKS.map(({ href, label, icon: Icon }) => {
          const isActive = pathname.startsWith(href)
          return (
            <Link
              key={href}
              href={href}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
              }`}
            >
              <Icon size={13} weight="fill" />
              {label}
            </Link>
          )
        })}
      </nav>

      <SystemStatus />
    </header>
  )
}
